// Activity — the score behind the Activity tab's first section, for an admin
// (a team's or the org's Activity) and for a learner (their own).
//  - Each measure has a % (its position on its 0-100 track).
//  - The overall % is their average: every measure counts equally, so the
//    five measures are 20% each.
//  - A % falls into a tier by the brackets below. The screen shows the % with
//    its tier beneath.
// If a measure is missing, the rest share the total equally between them.

import {
  measureLabel,
  PLATINUM_START,
  type MedalKey,
  type MedalSection
} from '../../../../../../components/gamification/scorecardData';

export type ActivityAudience = 'admin' | 'learner';

export interface TierBracket {
  key: MedalKey;
  from: number;
  to: number;
}

/** The tiers an organization or team can hold, lowest first, with the % bracket each covers. */
export const ACTIVITY_TIERS: TierBracket[] = [
  { key: 'wood', from: 0, to: 24 },
  { key: 'bronze', from: 25, to: 49 },
  { key: 'silver', from: 50, to: 74 },
  { key: 'gold', from: 75, to: 100 }
];

/** A learner can go one further: Exceptional is the top stretch of the track. */
export const LEARNER_ACTIVITY_TIERS: TierBracket[] = [
  ...ACTIVITY_TIERS.slice(0, 3),
  { key: 'gold', from: 75, to: PLATINUM_START - 1 },
  { key: 'platinum', from: PLATINUM_START, to: 100 }
];

export const activityTiers = (audience: ActivityAudience): TierBracket[] =>
  audience === 'learner' ? LEARNER_ACTIVITY_TIERS : ACTIVITY_TIERS;

export const tierForPercent = (percent: number, audience: ActivityAudience = 'admin'): MedalKey => {
  const tiers = activityTiers(audience);
  return (tiers.find((t) => percent >= t.from && percent <= t.to) ?? tiers[tiers.length - 1]).key;
};

export interface ActivityMeasure {
  /** The BE's name for the measure (the key for help copy and rankings). */
  name: string;
  /** What it is called on screen. */
  label: string;
  /** Whole percent, 0-100. */
  percent: number;
  tier: MedalKey;
  /** Its share of the overall score, in percent. */
  weight: number;
  /** percent x weight, in points of the overall score (one decimal). */
  contribution: number;
  /** The measure's own score as the BE words it ("5 days ago", "42%"). */
  value: string;
}

export interface TeamActivity {
  measures: ActivityMeasure[];
  /** Whole percent, or null with no measures. */
  percent: number | null;
  tier: MedalKey | null;
  strongest: ActivityMeasure | null;
  weakest: ActivityMeasure | null;
}

const clamp = (n: number) => Math.max(0, Math.min(100, n));

export const teamActivity = (
  sections: (Pick<MedalSection, 'name' | 'pos'> & { value?: string })[],
  audience: ActivityAudience = 'admin'
): TeamActivity => {
  const raw = sections.map((s) => ({
    name: s.name,
    value: s.value ?? '',
    percent: Math.round(clamp(s.pos))
  }));
  const weight = raw.length === 0 ? 0 : 100 / raw.length;
  const measures: ActivityMeasure[] = raw.map((m) => ({
    name: m.name,
    label: measureLabel(m.name),
    percent: m.percent,
    tier: tierForPercent(m.percent, audience),
    weight: Math.round(weight * 10) / 10,
    contribution: Math.round(m.percent * weight) / 100,
    value: m.value
  }));
  if (measures.length === 0) return { measures, percent: null, tier: null, strongest: null, weakest: null };

  const percent = Math.round(raw.reduce((sum, m) => sum + m.percent, 0) / raw.length);
  // Ties go to the first in the list.
  const strongest = measures.reduce((best, m) => (m.percent > best.percent ? m : best));
  const weakest = measures.reduce((worst, m) => (m.percent < worst.percent ? m : worst));
  return { measures, percent, tier: tierForPercent(percent, audience), strongest, weakest };
};
